import React, { useState, useEffect } from 'react';
import { HeatDto, TeamDto } from '../api/apis';
import { 
  fetchHeats, 
  createHeat, 
  deleteHeat, 
  fetchTeamsInHeat,
  addTeamToHeat,
  removeTeamFromHeat,
} from '../api/apis';
import { TeamSelector } from './TeamSelector';

export const HeatsView: React.FC = () => {
  const [heats, setHeats] = useState<HeatDto[]>([]);
  const [selectedHeat, setSelectedHeat] = useState<HeatDto | null>(null);
  const [selectedTeams, setSelectedTeams] = useState<TeamDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isTeamsLoading, setIsTeamsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  useEffect(() => {
    loadHeats();
  }, []);

  useEffect(() => {
    if (selectedHeat) {
      loadTeamsInHeat(selectedHeat.id);
    } else {
      setSelectedTeams([]);
    }
  }, [selectedHeat]);

  const loadHeats = async () => {
    try {
      setIsLoading(true);
      const heatsData = await fetchHeats();
      setHeats(heatsData);
      setError(null);
    } catch (err) {
      setError('Failed to load heats');
      console.error('Error loading heats:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTeamsInHeat = async (heatId: string) => {
    try {
      setIsTeamsLoading(true);
      const teams = await fetchTeamsInHeat(heatId);
      setSelectedTeams(teams);
      setError(null);
    } catch (err) {
      setError('Failed to load teams in heat');
      console.error('Error loading teams in heat:', err);
    } finally {
      setIsTeamsLoading(false);
    }
  };

  const handleAddHeat = async (name: string) => {
    try {
      const newHeat: HeatDto = {
        id: '', // The backend will generate this
        name
      };
      await createHeat(newHeat);
      // Instead of reloading, fetch just the new heat and add it to state
      const updatedHeats = await fetchHeats();
      setHeats(updatedHeats);
      setError(null);
    } catch (err) {
      setError('Failed to create heat');
      console.error('Error creating heat:', err);
    }
  };

  const handleDeleteHeat = async (heatId: string) => {
    try {
      await deleteHeat(heatId);
      // Update state directly by filtering out the deleted heat
      setHeats(prevHeats => prevHeats.filter(heat => heat.id !== heatId));
      if (selectedHeat?.id === heatId) {
        setSelectedHeat(null);
        setSelectedTeams([]);
      }
      setError(null);
    } catch (err) {
      setError('Failed to delete heat');
      console.error('Error deleting heat:', err);
    }
  };

  const handleTeamSelect = async (team: TeamDto) => {
    if (!selectedHeat) return;

    try {
      await addTeamToHeat(selectedHeat.id, team.id);
      // Refresh the teams list
      await loadTeamsInHeat(selectedHeat.id);
      setError(null);
    } catch (err) {
      if (err instanceof Error && err.message.includes('already assigned')) {
        setError(`Team "${team.name}" is already assigned to another heat`);
      } else {
        setError('Failed to add team to heat');
        console.error('Error adding team to heat:', err);
      }
    }
  };

  const handleTeamRemove = async (teamId: string) => {
    if (!selectedHeat) return;

    try {
      await removeTeamFromHeat(selectedHeat.id, teamId);
      // Refresh the teams list
      await loadTeamsInHeat(selectedHeat.id);
      setError(null);
    } catch (err) {
      setError('Failed to remove team from heat');
      console.error('Error removing team from heat:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="text-gray-600">Loading heats...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Heats Management</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Heat List */}
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold mb-4">Heats</h2>
          <div className="mb-4 flex gap-2">
            <input
              type="text"
              id="newHeatName"
              placeholder="New heat name"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={() => {
                const input = document.getElementById('newHeatName') as HTMLInputElement;
                if (input.value.trim()) {
                  handleAddHeat(input.value.trim());
                  input.value = '';
                }
              }}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              Add Heat
            </button>
          </div>
          <div className="max-h-[500px] overflow-y-auto">
          <div className="space-y-2">
            {heats.map((heat) => (
              <div
                key={heat.id}
                className={`p-3 rounded border cursor-pointer transition-colors ${
                  selectedHeat?.id === heat.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
                onClick={() => setSelectedHeat(heat)}
              >
                <div className="flex justify-between items-center">
                  <span className="font-medium">{heat.name}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteHeat(heat.id);
                    }}
                    className="text-red-500 hover:text-red-600"
                  >
                    Delete
                  </button>
                </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Heat Details */}
        <div className="bg-white rounded-lg shadow p-4">
          {selectedHeat ? (
            <div>
              <h2 className="text-lg font-semibold mb-4">{selectedHeat.name}</h2>
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-600">
                  {error}
                </div>
              )}
              {isTeamsLoading ? (
                <div className="text-gray-600">Loading teams...</div>
              ) : (
                <TeamSelector
                  selectedTeams={selectedTeams}
                  onTeamSelect={handleTeamSelect}
                  onTeamRemove={handleTeamRemove}
                />
              )}
            </div>
          ) : (
            <div className="text-gray-500 text-center py-8">
              Select a heat to manage teams
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 